import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, TrendingUp, Calendar, Award, Swords, Flame } from 'lucide-react';

function RankingLiga() {
  const [periodo, setPeriodo] = useState('semana');
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gamification/ranking`, { params: { periodo } })
      .then(res => setData(res.data))
      .catch(() => setData(null));
  }, [periodo]);

  const PERIODOS = [
    { id: 'semana', label: 'Semana' },
    { id: 'mes', label: 'Mes' },
    { id: 'total', label: 'Total' },
  ];

  return (
    <Card className="card-mk border-gold/30" data-testid="ranking-liga-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Swords className="w-5 h-5 text-gold" />
            Liga del Klan — Ranking XP
            <span className="text-[10px] font-normal bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full border border-gray-700">solo visible para ti</span>
          </CardTitle>
          <div className="flex gap-1">
            {PERIODOS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriodo(p.id)}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                  periodo === p.id ? 'bg-gold text-black border-gold' : 'border-gray-700 text-gray-400 hover:text-white'
                }`}
                data-testid={`ranking-periodo-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {(data?.ranking || []).map((r, index) => (
            <div key={r.user_id} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg" data-testid={`ranking-row-${index + 1}`}>
              <div className={`text-xl font-bold w-8 ${index === 0 ? 'text-gold' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-amber-600' : 'text-gray-500'}`}>
                #{index + 1}
              </div>
              <div className="relative flex-shrink-0">
                {r.foto_perfil ? (
                  <img src={`${BACKEND_URL}${r.foto_perfil}`} alt={r.nombre} className="w-10 h-10 rounded-full object-cover border-2 border-gold" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center font-bold text-gray-900">
                    {r.nombre?.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="absolute -bottom-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-gold text-black text-[10px] font-black flex items-center justify-center border border-black leading-none">
                  {r.nivel?.numero}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{r.nombre}</p>
                <p className="text-gray-400 text-xs">Nv {r.nivel?.numero} · {r.nivel?.nombre}</p>
              </div>
              {r.racha_actual > 0 && (
                <div className="flex items-center gap-0.5 text-orange-400 text-xs font-bold">
                  <Flame className="w-3.5 h-3.5" /> {r.racha_actual}
                </div>
              )}
              <div className="text-right">
                <p className="text-xl font-bold text-gold">{r.xp_periodo}</p>
                <p className="text-gray-400 text-[10px] uppercase">xp</p>
              </div>
            </div>
          ))}
          {(!data || data.ranking.length === 0) && (
            <p className="text-gray-400 text-center py-4">Sin XP registrado en este periodo todavía</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Estadisticas() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/attendance`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (day) => {
    const days = {
      'lunes': 'Lunes',
      'martes': 'Martes',
      'miercoles': 'Miércoles',
      'jueves': 'Jueves',
      'viernes': 'Viernes',
      'sabado': 'Sábado',
      'domingo': 'Domingo'
    };
    return days[day] || day;
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-gold mb-8" data-testid="estadisticas-title">
          Estadísticas de Asistencia
        </h1>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Liga del Klan - ranking XP (solo coach) */}
            <RankingLiga />

            {/* Resumen general */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="card-mk border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-900 rounded-lg">
                      <Calendar className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Total Entrenamientos</p>
                      <p className="text-3xl font-bold text-white">{stats.total_entrenamientos}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-mk border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-900 rounded-lg">
                      <TrendingUp className="w-8 h-8 text-green-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Asistencia Promedio</p>
                      <p className="text-3xl font-bold text-white">{stats.asistencia_promedio}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-mk border-gray-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-900 rounded-lg">
                      <Award className="w-8 h-8 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Entrenamientos Realizados</p>
                      <p className="text-3xl font-bold text-white">{stats.entrenamientos_realizados}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top usuarios */}
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  Top 5 Usuarios Más Activos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.top_usuarios.map((user, index) => (
                    <div key={user.email} className="flex items-center gap-4 p-3 bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold text-gold w-8">
                        #{index + 1}
                      </div>
                      {user.foto_perfil ? (
                        <img
                          src={`${BACKEND_URL}${user.foto_perfil}`}
                          alt={user.nombre}
                          className="w-12 h-12 rounded-full object-cover border-2 border-gold"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center font-bold text-gray-900">
                          {user.nombre.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-semibold">{user.nombre}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gold">{user.asistencias}</p>
                        <p className="text-gray-400 text-sm">asistencias</p>
                      </div>
                    </div>
                  ))}
                  {stats.top_usuarios.length === 0 && (
                    <p className="text-gray-400 text-center py-4">
                      No hay datos de asistencia todavía
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Asistencia por día */}
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-gold" />
                  Asistencia por Día de la Semana
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.asistencia_por_dia.map((day) => {
                    const maxAsistencia = 12;
                    const percentage = (day.promedio_asistencia / maxAsistencia) * 100;
                    
                    return (
                      <div key={day.dia} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-white font-semibold">{getDayLabel(day.dia)}</span>
                          <span className="text-gray-400">
                            {day.promedio_asistencia} / 12 ({day.total_entrenamientos} entrenamientos)
                          </span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {stats.asistencia_por_dia.length === 0 && (
                    <p className="text-gray-400 text-center py-4">
                      No hay datos de asistencia por día todavía
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="card-mk border-gray-800">
            <CardContent className="p-12 text-center">
              <p className="text-gray-400">Error al cargar estadísticas</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Estadisticas;
